import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { JobSearchResultCandidate } from '../data/api/JobSearchClient';
import { X, Search } from 'lucide-react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../../shared/components/Table/Table';

interface ApplicantSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: JobSearchResultCandidate[];
  onProceed: (selectedEmails: string[]) => void | Promise<void>;
  mode?: 'schedule' | 'download';
}

const getInitials = (firstName: string, lastName: string) =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";

export const ApplicantSelectionModal: React.FC<ApplicantSelectionModalProps> = ({ isOpen, onClose, candidates, onProceed, mode = 'schedule' }) => {
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [searchUser, setSearchUser] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedEmails([]);
      setSearchUser('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredCandidates = candidates.filter(c =>
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchUser.toLowerCase()) ||
    c.email_address.toLowerCase().includes(searchUser.toLowerCase())
  );

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedEmails(filteredCandidates.map(c => c.email_address));
    } else {
      setSelectedEmails([]);
    }
  };

  const toggleUser = (email: string) => {
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const handleProceed = async () => {
    try {
      setIsProcessing(true);
      await onProceed(selectedEmails);
    } finally {
      setIsProcessing(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15,23,42,0.5)', zIndex: 10001, display: 'flex',
        alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '720px',
          maxHeight: '92vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
              {mode === 'download' ? 'Download Resumes' : 'Schedule Interview'}
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              {mode === 'download' ? 'Select applicants to download their resumes as a ZIP file' : 'Select applicants to schedule an interview with'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px', borderRadius: '8px' }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
          <div className="lp-search" style={{ height: '40px', width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', display: 'flex', alignItems: 'center', padding: '0 12px' }}>
            <Search size={16} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search applicants by name or email..."
              value={searchUser}
              onChange={e => setSearchUser(e.target.value)}
              style={{ border: 'none', background: 'transparent', width: '100%', paddingLeft: '8px', outline: 'none', fontSize: '0.9rem' }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
          {filteredCandidates.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
              <p>No applicants found matching "{searchUser}"</p>
            </div>
          ) : (
            <Table className="learner-table" style={{ margin: 0 }}>
              <TableHeader style={{ position: 'sticky', top: 0, zIndex: 10, background: '#047857' }}>
                <TableRow>
                  <TableHead style={{ width: 40, paddingLeft: 24 }}>
                    <input
                      type="checkbox"
                      className="lp-checkbox"
                      onChange={toggleAll}
                      checked={filteredCandidates.length > 0 && filteredCandidates.every(c => selectedEmails.includes(c.email_address))}
                    />
                  </TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Email Address</TableHead>
                  <TableHead>Match Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.map(c => (
                  <TableRow key={c.candidate_id || c.email_address} data-state={selectedEmails.includes(c.email_address) ? 'selected' : undefined}>
                    <TableCell style={{ paddingLeft: 24 }}>
                      <input
                        type="checkbox"
                        className="lp-checkbox"
                        checked={selectedEmails.includes(c.email_address)}
                        onChange={() => toggleUser(c.email_address)}
                      />
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>
                          {getInitials(c.first_name, c.last_name)}
                        </div>
                        <strong style={{ color: '#0f172a' }}>{c.first_name} {c.last_name}</strong>
                      </div>
                    </TableCell>
                    <TableCell><span style={{ color: '#475569' }}>{c.email_address}</span></TableCell>
                    <TableCell><strong style={{ color: '#059669' }}>{parseFloat(String(c.match_score || 0)).toFixed(2)}%</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>
            {selectedEmails.length} {selectedEmails.length === 1 ? 'applicant' : 'applicants'} selected
          </span>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={isProcessing}
              style={{ background: '#fff', border: '1px solid #e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '8px', cursor: isProcessing ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem', opacity: isProcessing ? 0.6 : 1 }}
            >
              Cancel
            </button>
            <button
              onClick={handleProceed}
              disabled={selectedEmails.length === 0 || isProcessing}
              style={{ background: (selectedEmails.length > 0 && !isProcessing) ? 'var(--primary)' : '#cbd5e1', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '8px', cursor: (selectedEmails.length > 0 && !isProcessing) ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {isProcessing && (
                <div style={{ width: '14px', height: '14px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              )}
              {isProcessing ? 'Processing...' : (mode === 'download' ? 'Download Resumes' : 'Proceed to Schedule')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
